import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { LEFT_SIDE_NAV_WIDTH } from "skedyul-ui/ui";
import {
  SideNavigationSchemaType,
  SideNavigationPosition,
} from "@schemas/sideNavigation.schema";

export type SideNavigationType = {
  id: string;
  isOpen: boolean;
  position: SideNavigationPosition;
  runHandleClose?: boolean;
  width: string;
  mobileWidth?: string;
  schema: SideNavigationSchemaType;
  params: Record<string, unknown>;
};

export type SideNavigationState = {
  [key in SideNavigationPosition]: {
    id: string;
    isOpen: boolean;
    position: SideNavigationPosition;
    width: string;
    mobileWidth: string;
    schema: SideNavigationSchemaType;
    runHandleClose: boolean;
    params: Record<string, unknown>;
  };
};

const initialState: SideNavigationState = {
  left: {
    id: "left-side-navigation",
    isOpen: false,
    position: "left",
    width: LEFT_SIDE_NAV_WIDTH,
    mobileWidth: "300px",
    schema: "NAVIGATION_LIST",
    runHandleClose: false,
    params: {},
  },
  right: {
    id: "right-side-navigation",
    isOpen: false,
    position: "right",
    width: "250px",
    mobileWidth: "300px",
    schema: "CONTACT_LIST",
    runHandleClose: false,
    params: {},
  },
};

export const SideNavigationSlice = createSlice({
  name: "sideNavigation",
  initialState,
  reducers: {
    close: (
      state,
      action: PayloadAction<{ position: SideNavigationPosition }>,
    ) => {
      const { position } = action.payload;
      state[position].isOpen = false;
    },
    open: <T extends Record<string, unknown>>(
      state: SideNavigationState,
      action: PayloadAction<{
        position: SideNavigationPosition;
        schema: SideNavigationSchemaType;
        params?: T;
        props?: {
          width: string;
          mobileWidth?: string;
          runHandleClose?: boolean;
        };
      }>,
    ) => {
      const { position } = action.payload;

      state[position].isOpen = true;
      state[position].schema = action.payload.schema;
      state[position].params = action.payload.params || {};
      state[position].runHandleClose =
        action.payload.props?.runHandleClose || false;
      state[position].width =
        action.payload.props?.width ||
        state[position].width ||
        initialState[position].width;
      state[position].mobileWidth =
        action.payload.props?.mobileWidth ||
        state[position].mobileWidth ||
        initialState[position].mobileWidth;
    },
    updateParams: (
      state,
      action: PayloadAction<{
        position: SideNavigationPosition;
        params: Record<string, unknown>;
      }>,
    ) => {
      const { position, params } = action.payload;
      state[position].params = {
        ...(state[position].params || {}),
        ...params,
      } as Record<string, unknown>;
    },
    reset: () => initialState,
  },
});

// Action creators are generated for each case reducer function
export const { open, close, updateParams, reset } = SideNavigationSlice.actions;

export default SideNavigationSlice.reducer;
